import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';

export default function ProfilePage() {
  const [user, setUser] = useState<{ name?: string; phone?: string; referral_code?: string; member_id?: string; member_type?: string; team_num?: string; accounts?: { cash?: number; bucang?: number; huifu?: number;} } | null>(null);
  const [supportUrl, setSupportUrl] = useState<string>('/support');
  const [groupUrl, setGroupUrl] = useState<string>('/group');
  const [kycStatus, setKycStatus] = useState<string>('');
  const navigate = useNavigate();
  const loaded = useRef(false);
  const [showBalance, setShowBalance] = useState(true);
  const isExternal = (u: string) => /^https?:\/\//.test(u);

  const maskedPhone = useMemo(() => {
    const raw = user?.phone ?? '';
    const digits = raw.replace(/\D/g, '');
    if (/^\d{11}$/.test(digits)) return digits.slice(0, 3) + 'xxxx' + digits.slice(-4);
    if (digits.length >= 7) return digits.slice(0, 3) + 'xxxx' + digits.slice(-4);
    return digits ? digits.replace(/.(?=.{2})/g, '*') : '******';
  }, [user?.phone]);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    api.get('/me').then(({ data }) => setUser(data)).catch(() => navigate('/login'));
    api.get('/home-links').then(({ data }) => {
      if (data) {
        if (typeof data.support === 'string' && data.support.length) setSupportUrl(data.support);
        if (typeof data.group === 'string' && data.group.length) setGroupUrl(data.group);
      }
    }).catch(() => {});
    api.get('/kyc/status').then(({ data }) => setKycStatus(data?.status ?? '')).catch(() => {});
  }, [navigate]);

  function handleWithdraw(type: string) {
    navigate(`/sell?type=${type}`);
  }

  function handleDetail(type: string) {
    navigate(`/wallet?type=${type}`);
  }

  const formatBalance = (v: number) => showBalance
    ? v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '****';

  const kycLabel = kycStatus === 'approved' ? '已实名认证' : kycStatus === 'pending' ? '审核中' : '未实名认证';

  const quickActions = [
    { icon: '/images/me_shiming.png', label: '实名认证', sub: '查看详情 ▶', href: '/kyc' },
    { icon: '/images/me_tuandui.png', label: '我的团队', sub: '查看详情 ▶', href: '/team' },
    { icon: '/images/me_gongeneg.png', label: '银行卡绑定', sub: '查看详情 ▶', href: '/bind' },
    { icon: '/images/me_zijin.png', label: '资金明细', sub: '查看详情 ▶', href: '/wallet' },
  ];

  const otherMenuItems = [
    { icon: '/images/me_mia.png', label: '密码修改', href: '/security' },
    { icon: '/images/me_txiian.png', label: '提现记录', href: '/sell/history' },
    { icon: '/images/me_dizhi.png', label: '收货地址', href: '/address' },
    { icon: '/images/me_xiazia.png', label: 'APP下载', href: '/download' },
    { icon: '/images/me_kefu.png', label: '在线客服', href: supportUrl },
    { icon: '/images/me_qunliao.png', label: '官方群聊', href: groupUrl },
    { icon: '/images/me_rudou.png', label: '土豆下载', href: '/download' },
    { icon: '/images/me_shehzi.png', label: '系统设置', href: '/security' },
  ];

  return (
    <div className="max-w-md mx-auto min-h-screen relative bg-[url(/images/me_beijing.png)] bg-[length:100%_100%] bg-no-repeat bg-center pt-[230px] pb-6 relative">
      <AuthGuard />

      {/* Header with background */} 
      <div className="absolute top-0 left-0 right-0 px-4 pt-[20px] z-10">
        <div className="text-white text-lg block text-center mb-2">个人中心</div>
        {/* User info row */}
        <div className="flex items-center gap-3">
        <div className="flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-[#f5d998] flex items-center justify-center flex-shrink-0 border-2 border-[#f5d998]/50">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#8b0000" strokeWidth="1.5"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 4-7 8-7s8 3 8 7" /></svg>
          </div>
          {/* KYC badge */}
          <div className="-mt-[10px]">
            <span className="inline-block bg-[#c4161c] text-white text-[9px] px-3 py-0.5 rounded-full">{kycLabel}</span>
          </div>
          </div>
          
          <div className="flex-1 min-w-0 pt-1">
            <div className="flex items-center gap-1">
              <span className="text-white text-lg">{user?.name ?? '加载中...'}</span>
              <span className="relative block min-w-[150px] h-[50px] -mt-[10px]">
                <img src={"/images/me_badge.png"} className="w-[150px]" />
                <span className="absolute text-[9px] text-[#fff] top-[22px] left-1/2 transform -translate-x-1/2 w-full text-center">云家军战士{user?.team_num ?? '--'}班</span>
              </span>
            </div>
            <p className="text-white/80 text-xs mt-0.5">手机号码：{maskedPhone}</p>
            <p className="text-white/80 text-xs mt-0.5">邀请码：{user?.referral_code ?? '---'}</p>
          </div>
          <Link to="/checkin" className="flex-shrink-0">
            <img src="/images/me_meiri.png" alt="每日签到" className="h-8" />
          </Link>
        </div>
      </div> 

      {/* Wallet section */}
      <div className="px-4 -mt-6 relative z-10">
        <div className="bg-gradient-to-b from-[#fef0d8] to-[#fde8c4] rounded-xl p-3 shadow">
          {/* Wallet header */}
          <div className="flex items-center justify-between mb-3 border-b border-[#f5d998]/50 pb-1">
            <div className="flex items-center gap-1">
              <span className="text-[#940f10] font-bold text-lg">钱包</span>
              <button onClick={() => setShowBalance(!showBalance)}>
                <img src="/images/me_yanjing.png" alt="" className="w-5 h-4" style={{ opacity: showBalance ? 1 : 0.4 }} />
              </button>
            </div>
            <span className="text-[#940f10] text-[10px]">资产安全 · 稳健增值</span>
          </div> 
          {/* Two wallets side by side */}
          <div className="flex justify-around gap-3">
            {/* 我的现金 (buzhu) */}
            <div className="flex flex-col gap-1 items-center">
              <div className="flex gap-2 w-full items-center">
                <div className="flex items-center justify-center flex-shrink-0">
                  <img src="/images/me_xianj.png" alt="" className="w-8 h-8" />
                </div>
                <div className="flex flex-col items-start gap-1">
                  <span className="text-[#5a2d0c] text-xs">我的现金 (元)</span>
                  <span className="text-[#c4161c] text-md font-bold">¥{formatBalance(user?.accounts?.cash ?? 0)}</span>
                </div>
              </div>
              <div className="flex gap-2 justify-center w-full mt-1">
                <button onClick={() => handleWithdraw('cash')} className=" relative w-[70px]">
                  <img src="/images/me_tixan.png" alt="提现" className="w-full" />
                  <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[10px] text-white">提现</span>
                </button>
                <button onClick={() => handleDetail('cash')} className="relative w-[70px]">
                  <img src="/images/me_mingxi.png" alt="明细" className="w-full" />
                  <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[10px] text-[#cf1410]">明细</span>
                </button>
              </div>
            </div>

            <div className="w-[1px]">
                <img src="/images/me_shuxian.png" alt="" className="h-[80px]" />
            </div>

            {/* 我的补偿金 */} 
            <div className="flex flex-col gap-1 items-center">
              <div className="flex gap-2 w-full items-center">
                <div className="flex items-center justify-center flex-shrink-0">
                  <img src="/images/me_bucang.png" alt="" className="w-8 h-8" />
                </div>
                <div className="flex flex-col items-start gap-1">
                  <span className="text-[#5a2d0c] text-xs">我的补偿金 (元)</span>
                  <span className="text-[#c4161c] text-md font-bold">¥{formatBalance(user?.accounts?.bucang ?? 0)}</span>
                </div>
              </div>
              <div className="flex gap-2 justify-center w-full mt-1">
                <button onClick={() => handleWithdraw('bucang')} className=" relative w-[70px]">
                  <img src="/images/me_tixan.png" alt="提现" className="w-full" />
                  <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[10px] text-white">提现</span>
                </button>
                <button onClick={() => handleDetail('bucang')} className="relative w-[70px]">
                  <img src="/images/me_mingxi.png" alt="明细" className="w-full" />
                  <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[10px] text-[#cf1410]">明细</span>
                </button>
              </div>
            </div>
 
          </div>
        </div>

        {/* 恢复资产钱包 */}
        <div className="bg-[url('/images/me_qianbao.png')] bg-[length:100%_100%] bg-no-repeat rounded-xl p-4 shadow mt-2 pl-[100px]">
          <div className="flex items-center gap-3"> 
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#5a2d0c] font-bold text-sm">恢复资产股权价值</p>
                  <p className="text-[#8b5e3c] text-[10px] mt-0.5">恢复资产 · 助力未来</p>
                </div>
                
              </div>
              <div className="flex items-center justify-between">
                <p className="text-[#c4161c] text-lg font-bold mt-1">¥{formatBalance(user?.accounts?.huifu ?? 0)}</p>
                {/* <div className="flex gap-3 mt-2">
                  <button onClick={() => handleWithdraw('huifu')} className="flex items-center gap-1 relative">
                    <img src="/images/me_tixan.png" alt="提现" className="h-6" />
                    <span className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center text-[10px] text-white">提现</span>
                  </button>
                  <button onClick={() => handleDetail('huifu')} className="flex items-center gap-1 relative">
                    <img src="/images/me_mingxi.png" alt="明细" className="h-6" />
                    <span className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center text-[10px] text-[#cf1410]">明细</span>
                  </button>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 把握时代机遇 banner */}
      <div className="px-4 mt-1">
        <img src="/images/me_jiyu.png" alt="把握时代机遇 共享财富未来" className="w-full rounded-xl" />
      </div>

      {/* Quick actions 2x2 grid */}
      <div className="px-4 mt-3">
        <div className="bg-[url(/images/me_fenkuang.png)] bg-cover bg-no-repeat rounded-xl p-3 shadow">
          <div className="grid grid-cols-2 gap-3">
            {quickActions.map(({ icon, label, sub, href }) => (
              <Link key={label} to={href} className="flex items-center gap-3 h-[45px] pl-[10px]">
                <img src={icon} alt="" className="w-8 h-8 flex-shrink-0" />
                <div>
                  <p className="text-[#5a2d0c] text-sm font-bold">{label}</p>
                  <p className="text-[#8b5e3c] text-[10px]">{sub}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* 其他功能 section */}
      <div className="px-4 mt-3">
        <div className="bg-[url(/images/me_xiotng.png)] bg-[length:100%_100%] bg-no-repeat rounded-xl p-4 pt-[50px] h-[200px]"> 
          <div className="grid grid-cols-4 gap-y-4 mt-2">
            {otherMenuItems.map(({ icon, label, href }) => {
              const content = (
                <div className="flex flex-col items-center gap-1.5">
                  <img src={icon} alt={label} className="w-9 h-7" />
                  <span className="text-[#5a2d0c] text-[11px]">{label}</span>
                </div>
              );
              return isExternal(href) ? (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer">{content}</a>
              ) : (
                <Link key={label} to={href}>{content}</Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Logout button */}
      <div className="px-4 mt-4">
        <button
          onClick={() => { localStorage.removeItem('token'); navigate('/login'); }} 
        >
          <img src="/images/me_tuihux.png" alt="" className="w-full" /> 
        </button>
      </div>

      {/* Bottom tagline */}
      <div className="text-center py-6">
        <p className="text-white/50 text-xs">— 云数家园 · 与您共创美好生活 —</p>
      </div>
    </div>
  );
}
